import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { UtilsActionButtonsComponent } from '../utils-action-buttons/utils-action-buttons.component';
import { UtilsTableService } from '../../services/utils-table.service';
import { Tab, TabColumn } from '../../models/utils-tabs';
import { MetadataService } from '../../../../services/metadata.service';
import { catchError, NEVER, switchMap, tap } from 'rxjs';
import { TableDataResponse, TableSearchPayload } from '../../models/utils-table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectionModel } from '@angular/cdk/collections';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { NestedPropertyPipe } from '../../pipes/nested-property.pipe';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { UtilsTableSkeletonComponent } from '../utils-table-skeleton/utils-table-skeleton.component';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MetadataColumn, MetadataDataType } from '../../../../models/metadata';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ShipmentDetailsComponent } from '../../../shipment-details/shipment-details.component';
import { NgClass } from '@angular/common';
import { TableValueFormatterComponent } from '../table-value-formatter/table-value-formatter.component';
import { UtilsActionButtonsSkeletonComponent } from '../utils-action-buttons-skeleton/utils-action-buttons-skeleton.component';
import { EmittedFacets, FacetFilterComponent } from '../../../facet-filter/facet-filter.component';
import { FacetsByColumnKeyPipe } from '../../pipes/facets-by-column-key.pipe';
import {
  DateRangeFacetFilterComponent,
  EmittedDateRangeFacet
} from '../../../date-range-facet-filter/date-range-facet-filter.component';
import { TableType } from '../../../../models/shipment';
import { Shipment } from '../../../../pages/overview/models/shipment';
import { Container } from '../../../../pages/overview/models/container';
import { ContainerDetailsComponent } from '../../../../pages/containers/components/container-details/container-details.component';
import { ActivatedRoute } from '@angular/router';
import { ArchiveFiltersComponent } from '../archive-filters/archive-filters.component';
import { EllipsisTooltipDirective } from '../../../../directives/ellipsis-tooltip.directive';
import { Order } from '../../../../pages/overview/models/order';
import { OrderDetailsComponent } from '../../../../pages/orders/components/order-details/order-details.component';
import { OrderException } from '../../../../pages/overview/models/order-exception';
import { OrderExceptionDetailsComponent } from '../../../../pages/order-exceptions/components/order-exception-details/order-exception-details.component';
import { GoogleTagManagerService } from '../../../../services/google-tag-manager.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StripHtmlPipe } from '../../../../pages/order-exceptions/pipes/strip-html.pipe';
import { ErrorHandlerService } from '../../../../utils/cross-xss-script-error-handler-service';

export interface TableColumn extends TabColumn {
  accessPath: string;
  name: string;
  sortable: boolean;
  dataType: MetadataDataType;
  facetable: boolean;
}

type OneOfEntity = Shipment | Container | Order | OrderException;

@Component({
  selector: 'app-utils-table',
  standalone: true,
  templateUrl: './utils-table.component.html',
  imports: [
    UtilsActionButtonsComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    NestedPropertyPipe,
    MatPaginator,
    MatCheckbox,
    MatProgressSpinner,
    UtilsTableSkeletonComponent,
    MatSort,
    MatSortHeader,
    MatDrawerContainer,
    ShipmentDetailsComponent,
    MatDrawer,
    NgClass,
    MatDrawerContent,
    TableValueFormatterComponent,
    UtilsActionButtonsSkeletonComponent,
    FacetFilterComponent,
    FacetsByColumnKeyPipe,
    DateRangeFacetFilterComponent,
    ContainerDetailsComponent,
    ArchiveFiltersComponent,
    MatFooterCell,
    MatFooterCellDef,
    MatFooterRow,
    MatFooterRowDef,
    EllipsisTooltipDirective,
    OrderDetailsComponent,
    OrderExceptionDetailsComponent,
    StripHtmlPipe
  ],
  styleUrl: './utils-table.component.scss'
})
export class UtilsTableComponent implements OnChanges, AfterViewInit {
  @ViewChild('drawer')
  set drawerEl(drawer: MatDrawer | undefined) {
    this.matDrawer = drawer;

    if (drawer && this.shouldOpenDrawerWhenReady) {
      this.shouldOpenDrawerWhenReady = false;
      this.onRowClick(this.tableData.data[0] as OneOfEntity);
    }
  }

  get drawer(): MatDrawer | undefined {
    return this.matDrawer;
  }
  @ViewChildren('facetFilter') facetFilters!: QueryList<FacetFilterComponent>;
  @ViewChildren('dateRangeFacetFilter') dateRangeFacetFilters!: QueryList<DateRangeFacetFilterComponent>;
  @Input() tab: Tab | null = null;
  @Input() tabIndex: number | null = null;
  @Input() type: TableType | null = null;

  tableData!: TableDataResponse;
  tableColumns: TableColumn[] = [];
  columnsToDisplay: string[] = [];
  isTableInitiating = true;
  isLoading = true;
  selection = new SelectionModel<TableDataResponse['data']>(true, []);
  rowClicked: OneOfEntity | null = null;
  selectedItems: Shipment[] | Container[] | Order[] | OrderException[] = [];
  searchValueFromUrl = '';

  private gtmService = inject(GoogleTagManagerService);
  private utilsTableService = inject(UtilsTableService);
  private metadataService = inject(MetadataService);
  private matSnackBar = inject(MatSnackBar);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private takenFromUrl = false;
  private matDrawer?: MatDrawer;
  private shouldOpenDrawerWhenReady = false;
  private errorHandler = inject(ErrorHandlerService);

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tab'].currentValue) {
      this.searchValueFromUrl = this.route.firstChild?.snapshot.paramMap.get('id') ?? '';
      this.utilsTableService
        .getPageConfigFor(this.tab!.id)
        .pipe(
          tap(() => this.refreshView()),
          switchMap(config => {
            const searchValue =
              !this.takenFromUrl && this.searchValueFromUrl
                ? ((this.takenFromUrl = true), this.searchValueFromUrl)
                : config.search?.value || '';
            const search: TableSearchPayload = { columnKey: config.search?.columnKey || null, value: searchValue };
            return this.type === 'Archive'
              ? this.utilsTableService.getArchivedTableData(
                  { pageNumber: config.pageNumber, pageSize: config.pageSize, search },
                  this.tab!.type
                )
              : this.utilsTableService.getTableData({ ...config, search, tabId: this.tab!.id }, this.type!);
          }),
          tap(items => {
            this.tableData = items;
            this.isLoading = false;
          }),
          switchMap(() => this.generateColumns()),
          tap(() => {
            if (this.tableData.data.length === 1 && this.takenFromUrl) {
              this.openDrawer();
            }
          }),
          catchError(error => this.handleError(error)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  ngAfterViewInit() {
    this.handleSelectionChange();
  }

  handleSelectionChange() {
    this.selection.changed
      .pipe(
        tap(() => (this.selectedItems = this.selection.selected)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onPaginationChange({ pageIndex, pageSize }: PageEvent) {
    this.utilsTableService.updatePageConfig(this.tab!.id, { pageSize, pageNumber: pageIndex + 1 });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.tableData.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.tableData.data);
  }

  onSort({ active, direction }: Sort) {
    if (direction) {
      this.utilsTableService.updatePageConfig(this.tab!.id, { sort: { columnKey: active, direction } });
    } else {
      this.utilsTableService.updatePageConfig(this.tab!.id, { sort: null });
    }
  }

  onRowClick(row: OneOfEntity) {
    if (!this.matDrawer) {
      return;
    }
    if (this.matDrawer.opened) {
      if (this.isEntityAlreadyOpened(row)) {
        this.closeDrawer();
      } else {
        this.rowClicked = row;
      }
    } else {
      this.rowClicked = row;
      this.matDrawer.open();
    }

    this.handleGtmEvent();
  }

  closeDrawer() {
    if (this.drawer?.opened) {
      this.drawer.close();
      this.rowClicked = null;
    }
  }

  onFacetsSelect(facets: EmittedFacets) {
    this.utilsTableService.setTextFacets(this.tab!.id, facets);
  }

  onDateRangeFacetSelect(facet: EmittedDateRangeFacet) {
    this.utilsTableService.setDateRangeFacet(this.tab!.id, facet);
  }

  isEntityAlreadyOpened(row: OneOfEntity) {
    if (this.rowClicked) {
      switch (this.tab?.type) {
        case 'Shipments':
          return (
            'shipmentNo' in row && 'shipmentNo' in this.rowClicked && row.shipmentNo === this.rowClicked?.shipmentNo
          );
        default:
          return 'id' in row && 'id' in this.rowClicked && row.id === this.rowClicked?.id;
      }
    }

    return false;
  }

  shipmentNo() {
    return (this.rowClicked as Shipment)?.shipmentNo || null;
  }

  containerId() {
    return (this.rowClicked as Container)?.id || null;
  }

  orderId() {
    return (this.rowClicked as Order)?.id || null;
  }

  orderExceptionId() {
    return (this.rowClicked as OrderException)?.id || null;
  }

  onClearFilters() {
    this.facetFilters.forEach(filter => filter.clear());
    this.dateRangeFacetFilters.forEach(filter => filter.reset());
  }

  private generateColumns() {
    const tabColumnKeys = this.tab!.columns.map(column => column.columnKey);
    return this.metadataService.getColumnsBy(tabColumnKeys, this.tab!.type).pipe(
      tap(metadataColumns => {
        this.tableColumns = this.generateTableColumns(this.tab!.columns, metadataColumns);
        this.columnsToDisplay = this.tableColumns.map(column => column.columnKey);
        if (this.type !== 'Archive') {
          this.columnsToDisplay.unshift('select');
        }
        this.isTableInitiating = false;
      })
    );
  }

  private generateTableColumns(tabColumns: TabColumn[], metadataColumns: MetadataColumn[]) {
    return tabColumns
      .map(column => {
        const metadataColumn = metadataColumns.find(col => col.key === column.columnKey);
        return {
          ...column,
          accessPath: metadataColumn?.accessPath || '',
          name: metadataColumn?.name || '',
          sortable: metadataColumn?.sortable || false,
          facetable: metadataColumn?.facetable || false,
          dataType: metadataColumn?.dataType || 'Text'
        };
      })
      .sort((a, b) => a.order - b.order);
  }

  private refreshView() {
    this.isLoading = true;
    this.selection.clear();
    this.closeDrawer();
  }

  private handleError(error: HttpErrorResponse) {
    console.error(error);
    this.isLoading = false;
    this.errorHandler.showApiError(
      error,
      'Could not get the data, please try again later',
      [
        {
          key: 'search.value',
          name: 'Search'
        }
      ],
      (fieldName, options) => options.find(x => x.key === fieldName)?.name
    );
    return NEVER;
  }

  private handleGtmEvent() {
    if (this.type === 'Shipments') {
      this.gtmService.pushEvent({
        event: 'shipment_details',
        type: {
          tab_viewed: 'Details',
          tab_entry: 'Shipment'
        }
      });
    } else if (this.type === 'Containers') {
      this.gtmService.pushEvent({
        event: 'container_details',
        type: { tab_viewed: 'Details' }
      });
    }
  }

  private openDrawer(): void {
    if (this.drawer) {
      this.onRowClick(this.tableData.data[0] as OneOfEntity);
      return;
    }

    this.shouldOpenDrawerWhenReady = true;
  }
}
